import React, { useEffect, useMemo, useState } from 'react'
import { Alert, Tab } from 'react-bootstrap'
import { Tabs } from '../../Tabs/Tabs'
import { Spinner } from '../../Spinner/Spinner'
import { useAppDispatch, useAppSelector } from '../../../store/hooks'
import { selectAuthenticatedUser } from '../../../store/auth/authSlice'
import { addToast } from '../../../store/toast/toastSlice'
import { useZocksAuth } from '../../ScopedBlock/hooks'
import {
  useDeleteAccountParametersMutation,
  useDeleteUserParametersMutation,
  useGetCustomWorkflowParametersQuery,
  usePutAccountParametersMutation,
  usePutUserParametersMutation,
  type CustomWorkflowParametersView,
} from '../../../store/customWorkflow/customWorkflows'
import { applyMergePatch, deepEqual, defaultsFromSchema, type JsonObject } from './mergePatch'
import { WorkflowParametersForm } from './WorkflowParametersForm'

export interface WorkflowParametersSectionProps {
  accountId: string
  workflowId: string
  onHasParameters: (has: boolean) => void
  onDirtyChange: (dirty: boolean) => void
}

interface LayerConfig {
  schema: Record<string, unknown>
  baseline: Record<string, unknown>
  layer: Record<string, unknown> | null
}

const detailFromError = (e: unknown): string | null => {
  if (typeof e === 'object' && e !== null && 'data' in e) {
    const data = (e as { data?: { detail?: unknown; errors?: Array<{ message?: unknown }> } }).data
    const detail = data?.detail
    if (detail !== undefined && detail !== null) {
      return String(detail)
    }
    if (Array.isArray(data?.errors)) {
      const messages = data.errors.map((error) => error?.message).filter((message): message is string => typeof message === 'string')
      if (messages.length > 0) {
        return messages.join(', ')
      }
    }
  }
  return null
}

export const WorkflowParametersSection: React.FC<WorkflowParametersSectionProps> = (props) => {
  const currentUserId = useAppSelector(selectAuthenticatedUser)?.id
  return <WorkflowParametersSectionContent key={`${props.accountId}|${props.workflowId}|${currentUserId}`} {...props} />
}

const WorkflowParametersSectionContent: React.FC<WorkflowParametersSectionProps> = ({ accountId, workflowId, onHasParameters, onDirtyChange }) => {
  const dispatch = useAppDispatch()
  const currentUserId = useAppSelector(selectAuthenticatedUser)?.id
  const { hasScope } = useZocksAuth()
  const isAdmin = hasScope('**', 'custom_workflow:admin')

  const { data, isLoading, isError, error } = useGetCustomWorkflowParametersQuery({
    accountId,
    workflowId,
    userId: currentUserId,
    suppressGlobalErrorToast: true,
  })
  const isNotFound = isError && typeof error === 'object' && error !== null && 'status' in error && error.status === 404

  const [putAccountParameters, { isLoading: savingAccount }] = usePutAccountParametersMutation()
  const [deleteAccountParameters, { isLoading: resettingAccount }] = useDeleteAccountParametersMutation()
  const [putUserParameters, { isLoading: savingUser }] = usePutUserParametersMutation()
  const [deleteUserParameters, { isLoading: resettingUser }] = useDeleteUserParametersMutation()

  const [activeTab, setActiveTab] = useState<'account' | 'user'>('user')
  const [accountDirty, setAccountDirty] = useState(false)
  const [userDirty, setUserDirty] = useState(false)
  const [accountExternallyUpdated, setAccountExternallyUpdated] = useState(false)
  const [userExternallyUpdated, setUserExternallyUpdated] = useState(false)
  const [trackedData, setTrackedData] = useState<CustomWorkflowParametersView | undefined>(data)

  if (data !== trackedData) {
    const previousData = trackedData
    setTrackedData(data)
    const accountLayerChanged = !deepEqual(previousData?.accountParameters, data?.accountParameters)
    const userLayerChanged = !deepEqual(previousData?.userParameters, data?.userParameters)
    if (accountDirty && accountLayerChanged) {
      setAccountExternallyUpdated(true)
    }
    if (userDirty && (userLayerChanged || accountLayerChanged)) {
      setUserExternallyUpdated(true)
    }
  }

  if (!accountDirty && accountExternallyUpdated) {
    setAccountExternallyUpdated(false)
  }
  if (!userDirty && userExternallyUpdated) {
    setUserExternallyUpdated(false)
  }

  const hasParameters = data ? true : isNotFound ? false : null
  useEffect(() => {
    if (hasParameters !== null) {
      onHasParameters(hasParameters)
    }
  }, [hasParameters, onHasParameters])

  const dirty = accountDirty || userDirty
  useEffect(() => {
    onDirtyChange(dirty)
  }, [dirty, onDirtyChange])

  const layers = useMemo(() => {
    if (!data) {
      return null
    }
    const defaults = defaultsFromSchema(data.parameterSchema)
    const account: LayerConfig = { schema: data.parameterSchema, baseline: defaults, layer: data.accountParameters }
    const user: LayerConfig = {
      schema: data.parameterSchema,
      baseline: applyMergePatch(defaults, data.accountParameters ?? {}) as JsonObject,
      layer: data.userParameters,
    }
    return { account, user }
  }, [data])

  const handleSaveAccount = async (patch: Record<string, unknown>): Promise<boolean> => {
    try {
      await putAccountParameters({ accountId, workflowId, parameters: patch, suppressGlobalErrorToast: true }).unwrap()
      return true
    } catch (e) {
      dispatch(addToast({ variant: 'error', title: 'Could not save parameters', messages: [detailFromError(e) ?? 'Please try again.'] }))
      return false
    }
  }

  const handleResetAccount = async (): Promise<boolean> => {
    try {
      await deleteAccountParameters({ accountId, workflowId, suppressGlobalErrorToast: true }).unwrap()
      return true
    } catch (e) {
      dispatch(addToast({ variant: 'error', title: 'Could not reset parameters', messages: [detailFromError(e) ?? 'Please try again.'] }))
      return false
    }
  }

  const handleSaveUser = async (patch: Record<string, unknown>): Promise<boolean> => {
    if (!currentUserId) {
      dispatch(addToast({ variant: 'error', title: 'Could not save parameters', messages: ['Please try again.'] }))
      return false
    }
    try {
      await putUserParameters({ accountId, workflowId, userId: currentUserId, parameters: patch, suppressGlobalErrorToast: true }).unwrap()
      return true
    } catch (e) {
      dispatch(addToast({ variant: 'error', title: 'Could not save parameters', messages: [detailFromError(e) ?? 'Please try again.'] }))
      return false
    }
  }

  const handleResetUser = async (): Promise<boolean> => {
    if (!currentUserId) {
      dispatch(addToast({ variant: 'error', title: 'Could not reset parameters', messages: ['Please try again.'] }))
      return false
    }
    try {
      await deleteUserParameters({ accountId, workflowId, userId: currentUserId, suppressGlobalErrorToast: true }).unwrap()
      return true
    } catch (e) {
      dispatch(addToast({ variant: 'error', title: 'Could not reset parameters', messages: [detailFromError(e) ?? 'Please try again.'] }))
      return false
    }
  }

  if (isLoading) {
    return <Spinner size='sm' />
  }

  if (isNotFound && !data) {
    return null
  }

  if (isError && !isNotFound && !data) {
    return (
      <Alert variant='warning' className='mb-0'>
        Could not load parameters.
      </Alert>
    )
  }

  if (!data || !layers) {
    return null
  }

  return (
    <div className='border-top pt-3'>
      {isError ? (
        <Alert variant='warning' className='mb-2'>
          Could not refresh parameters — showing the last loaded values.
        </Alert>
      ) : null}
      <div className='fw-semibold'>Parameters</div>
      <div className='paragraph-small zocks-text-gray-600 mb-2'>Customize how this workflow behaves. Your own values override account-level values.</div>
      <Tabs id='workflow-parameters-tabs' activeKey={activeTab} onSelect={(key) => setActiveTab(key === 'account' ? 'account' : 'user')}>
        <Tab eventKey='user' title='Your own' />
        {isAdmin ? <Tab eventKey='account' title='Account level' /> : null}
      </Tabs>
      <div data-testid='workflow-parameters-pane-user' className={activeTab === 'user' ? undefined : 'd-none'}>
        <WorkflowParametersForm
          idPrefix='user'
          layerName='user'
          schema={layers.user.schema}
          baseline={layers.user.baseline}
          layer={layers.user.layer}
          saving={savingUser || resettingUser}
          onSave={handleSaveUser}
          onReset={handleResetUser}
          onDirtyChange={setUserDirty}
          externallyUpdated={userExternallyUpdated}
          onAcknowledgeExternalUpdate={() => setUserExternallyUpdated(false)}
        />
      </div>
      {isAdmin ? (
        <div data-testid='workflow-parameters-pane-account' className={activeTab === 'account' ? undefined : 'd-none'}>
          <WorkflowParametersForm
            idPrefix='account'
            layerName='account'
            schema={layers.account.schema}
            baseline={layers.account.baseline}
            layer={layers.account.layer}
            saving={savingAccount || resettingAccount}
            onSave={handleSaveAccount}
            onReset={handleResetAccount}
            onDirtyChange={setAccountDirty}
            externallyUpdated={accountExternallyUpdated}
            onAcknowledgeExternalUpdate={() => setAccountExternallyUpdated(false)}
          />
        </div>
      ) : null}
      {data.effective.warnings.length > 0 ? (
        <Alert variant='warning' className='mt-3 mb-0'>
          {data.effective.warnings.map((warning, index) => (
            <div key={index}>{warning}</div>
          ))}
        </Alert>
      ) : null}
      <details className='mt-3'>
        <summary>Effective configuration</summary>
        <pre className='paragraph-small mb-0 overflow-x-auto'>{JSON.stringify(data.effective.parameters, null, 2)}</pre>
      </details>
    </div>
  )
}
